import { PageHeader } from '@/shared/components/ui/PageHeader';
import { useMemo, useState } from 'react';
import { Sparkles } from 'lucide-react';
import { useSearchParams } from 'react-router-dom';
import { cn } from '@/shared/utils/utils';
import { AI_SECTIONS, type AISectionId } from '../constants';
import { resolveAIFeatureDeepLink, visibleAIFeatures } from '../authority';
import { useUser } from '@/shared/hooks/useAuth';
import {
  BriefingCard,
  LeadScoresCard,
  DealIntelCard,
  CallOptimizerCard,
  NicheCard,
  ScrapeAdvisorCard,
  AnomaliesCard,
} from '../components/sections/AnalyticsSections';
import { AssistantSection, MeetingAssistantSection } from '../components/sections/GenerativeSections';
import { LeadInsightsTool, FollowUpDraftTool, CallCoachTool, IntentParserTool } from '../components/sections/GenerativeTools';
import { CapabilitiesSection } from '../components/sections/CapabilitiesSection';

export function AIWorkspacePage() {
  const [section, setSection] = useState<AISectionId>('overview');
  const [searchParams, setSearchParams] = useSearchParams();
  const user = useUser();
  const dept = user?.department || 'sales';

  const sections = useMemo(() => {
    const authorityVisible = visibleAIFeatures(AI_SECTIONS, user);
    if (dept === 'sales') return authorityVisible;
    // Department affects organizational scope only; authority is resolved above from role.
    const nonSalesTabs = new Set(['overview', 'assistant', 'insights', 'meeting', 'settings']);
    return authorityVisible.filter((candidate) => nonSalesTabs.has(candidate.id));
  }, [dept, user]);

  const requestedFeature = resolveAIFeatureDeepLink(AI_SECTIONS, user, searchParams.get('section'));
  const activeSection = sections.some(({ id }) => id === requestedFeature?.id)
    ? requestedFeature!.id
    : sections.some(({ id }) => id === section)
      ? section
      : 'overview';

  const selectSection = (nextSection: AISectionId) => {
    setSection(nextSection);
    const nextParams = new URLSearchParams(searchParams);
    if (nextSection === 'overview') nextParams.delete('section');
    else nextParams.set('section', nextSection);
    setSearchParams(nextParams);
  };

  return (
    <div className="flex h-full flex-col gap-4 pb-16">
      <PageHeader
        icon={Sparkles}
        eyebrow="Workspace"
        title="AI Workspace"
        description={<>Insights, intelligence and the AI assistant — one workspace.</>}
      />

      <div className="flex flex-wrap gap-1 border-b border-outline pb-2">
        {sections.map((candidate) => (
          <button
            key={candidate.id}
            onClick={() => selectSection(candidate.id)}
            className={cn(
              'flex items-center gap-1.5 rounded px-3 py-1.5 text-data-md font-medium transition-colors',
              activeSection === candidate.id
                ? 'bg-primary-container text-on-primary-container'
                : 'text-on-surface-variant hover:bg-surface-variant',
            )}
          >
            <candidate.icon className="h-4 w-4" />
            {candidate.label}
          </button>
        ))}
      </div>

      <div className="flex-1 overflow-y-auto">
        {activeSection === 'overview' && (
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
            <BriefingCard />
            {dept === 'sales' && <DealIntelCard />}
          </div>
        )}

        {activeSection === 'assistant' && (
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
            <AssistantSection />
            <IntentParserTool />
          </div>
        )}

        {activeSection === 'insights' && (
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
            <BriefingCard />
            <AnomaliesCard />
          </div>
        )}

        {activeSection === 'leads' && (
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
            <LeadScoresCard />
            <div className="flex flex-col gap-4">
              <LeadInsightsTool />
              <FollowUpDraftTool />
            </div>
          </div>
        )}

        {activeSection === 'deals' && <DealIntelCard />}

        {activeSection === 'optimization' && (
          <div className="grid grid-cols-1 gap-4">
            <CallOptimizerCard />
            <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
              <NicheCard />
              <ScrapeAdvisorCard />
            </div>
            <CallCoachTool />
          </div>
        )}

        {activeSection === 'meeting' && <MeetingAssistantSection />}
        {activeSection === 'settings' && <CapabilitiesSection />}
      </div>
    </div>
  );
}
